import React, { useState, useEffect, useRef } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Box, Typography, CircularProgress, LinearProgress } from '@mui/material';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';

import { serverServicesAPI } from '../services/api';
import { useIsCloudAdmin } from '../context/PermissionsContext';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const POLL_MS = 3000;

// Stop (end the service and everything it started, wait 30 seconds, check) and start a STARLIMS service. Cloud Services administrators only;
// every request needs a reason and is recorded by the portal. The work runs on the server for up to a minute, so the dialog follows it.
function ServiceControl({ customerCode, host, serviceKey, serviceLabel, state, jobCount, onChanged }) {
  const isAdmin = useIsCloudAdmin();
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState('stop');
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);          // the audit row being followed
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  if (!isAdmin || !state) return null;

  const running = state === 'Running';
  const busy = submitting || (run && run.status === 'running');
  const form = { customer_code: customerCode, host, service: serviceKey };

  const follow = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const row = (await serverServicesAPI.action(id)).data;
        setRun(row);
        if (row.status === 'running') follow(id); else if (onChanged) onChanged();
      } catch (err) { setError(errText(err, 'Could not follow the request')); }
    }, POLL_MS);
  };

  const begin = (which) => {
    setAction(which); setError(null); setRun(null); setConfirm('');
    if (which === 'start' && !reason) setReason('Start after stop');
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const body = { ...form, reason, ticket: ticket || null, confirm_host: confirm };
      const res = await (action === 'stop' ? serverServicesAPI.stop(body) : serverServicesAPI.start(body));
      setRun({ id: res.data.id, status: 'running', action });
      follow(res.data.id);
    } catch (err) { setError(errText(err, 'The request was not accepted')); }
    setSubmitting(false);
  };

  const close = () => { if (!busy) { setOpen(false); clearTimeout(timer.current); } };
  const done = run && run.status !== 'running';
  const ended = run?.detail?.ended || [];
  const verb = action === 'stop' ? 'Stop' : 'Start';

  return (
    <>
      {running
        ? <Button size="small" color="error" startIcon={<StopCircleIcon />} onClick={() => begin('stop')}>Stop service</Button>
        : <Button size="small" color="success" startIcon={<PlayCircleIcon />} onClick={() => begin('start')}>Start service</Button>}
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="control-title">
        <DialogTitle id="control-title">{verb} {serviceLabel} on {host}</DialogTitle>
        <DialogContent>
          {!run && action === 'stop' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This ends the service and every program it started{jobCount != null ? ` (${jobCount} job${jobCount === 1 ? '' : 's'} running now)` : ''}, even if a job is part way through,
              then waits 30 seconds and checks that nothing is left. It does not start the service again until you ask.
            </Alert>
          )}
          {!run && action === 'start' && <Alert severity="info" sx={{ mb: 2 }}>The service will be started and checked for up to 30 seconds.</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!run && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} required multiline minRows={2} fullWidth
                helperText="Recorded with your name in the audit trail" />
              <TextField label="Ticket (optional)" value={ticket} onChange={(e) => setTicket(e.target.value)} fullWidth />
              {action === 'stop' && (
                <TextField label={`Type ${host} to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} fullWidth autoComplete="off" />
              )}
            </Box>
          )}

          {run && !done && (
            <Box>
              <Typography sx={{ mb: 1 }}>{action === 'stop' ? 'Stopping the service and its programs. This takes about 40 seconds.' : 'Starting the service.'}</Typography>
              <LinearProgress />
            </Box>
          )}
          {done && run.status === 'succeeded' && (
            <Alert severity="success">
              {action === 'stop'
                ? `Stopped. ${ended.length} process${ended.length === 1 ? ' was' : 'es were'} ended and the service reports ${run.detail?.after}.`
                : `Started. The service reports ${run.detail?.after}${run.detail?.pid ? `, running as process ${run.detail.pid}` : ''}.`}
            </Alert>
          )}
          {done && run.status === 'failed' && <Alert severity="error">{run.error || 'The request failed.'} Refresh the page to see the service as it is now.</Alert>}
        </DialogContent>
        <DialogActions>
          {!run && <Button onClick={close}>Cancel</Button>}
          {!run && (
            <Button variant="contained" color={action === 'stop' ? 'error' : 'success'} onClick={submit}
              disabled={submitting || reason.trim().length < 5 || (action === 'stop' && confirm.trim().toUpperCase() !== host.toUpperCase())}>
              {submitting ? <CircularProgress size={18} /> : `${verb} service`}
            </Button>
          )}
          {done && action === 'stop' && run.status === 'succeeded' && (
            <Button variant="contained" color="success" onClick={() => { setRun(null); setAction('start'); setReason('Start after stop'); setTicket(ticket); }}>Start it again…</Button>
          )}
          {run && <Button onClick={close} disabled={!done}>Close</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ServiceControl;
